import { useEffect, useMemo, useState } from "preact/hooks";
import type { CoreData, InsightsData, NameDetailData, NameEntry, SexIdx } from "../data/types";
import { balance, balanceWords, familyOf, landmark, lately, type Generation } from "../data/analysis";
import { entryFor, loadInsights } from "../data/load";
import { SEX_WORD, fmt } from "../data/format";
import { openName } from "../state/router";
import { HistoryChart } from "./charts";

function useInsights(core: CoreData): InsightsData | null {
  const [ins, setIns] = useState<InsightsData | null>(null);
  useEffect(() => {
    let live = true;
    loadInsights()
      .then((d) => live && setIns(d))
      .catch(() => {
        /* the rest of the page doesn't need it */
      });
    return () => {
      live = false;
    };
  }, [core]);
  return ins;
}

/**
 * What a name's figures say beyond its rank: how the last few years have gone, when its babies
 * were born, how common it is with every spelling counted, and the balance of girls and boys.
 */
export function NameStory({
  core,
  k,
  name,
  entry,
  data,
  si,
  gen,
  shaded,
}: {
  core: CoreData;
  k: string;
  name: string;
  entry: NameEntry | undefined;
  data: NameDetailData | null | undefined;
  si: SexIdx;
  /** worked out by the page, which also shades it on the chart */
  gen: Generation | null;
  /** whether the chart above shades the generation's years */
  shaded: boolean;
}) {
  const ins = useInsights(core);
  const word = SEX_WORD[si];
  const latest = core.latest;
  const start = core.country.since;
  const series = si === 0 ? data?.F?.t : data?.M?.t;

  const run = useMemo(() => lately(series, latest), [series, latest]);
  const mark = useMemo(() => landmark(series, latest), [series, latest]);
  const mix = useMemo(() => balance(data?.F?.t, data?.M?.t, core.country.id === "us" ? 30 : 10), [data, core]);
  const fam = ins ? familyOf(ins, k, si) : null;

  const lines: string[] = [];
  if (run) lines.push(`Given to ${run.dir === "up" ? "more" : "fewer"} ${word} each year for the last ${run.years} years.`);
  if (mark) {
    const first = series?.[0] ?? start;
    if (mark.kind === "most")
      lines.push(
        mark.since === null
          ? `${latest} had the most ${word} named ${name} of any year since ${first <= start ? `the records began in ${start}` : `it first appeared, in ${first}`}.`
          : `${latest} had more ${word} named ${name} than any year since ${mark.since}.`,
      );
    else
      lines.push(
        mark.since === null
          ? `${latest} had the fewest ${word} named ${name} since ${first <= start ? `the records began in ${start}` : `it first appeared, in ${first}`}.`
          : `${latest} had fewer ${word} named ${name} than any year since ${mark.since}.`,
      );
  }

  // worth showing when other spellings add a real share, or lift its rank
  const own = entry?.r[si] ?? 0;
  const ownCount = fam?.members.find((m) => m.key === k)?.count ?? 0;
  const showFam = !!fam && fam.members.length > 1 && (fam.total - ownCount >= fam.total * 0.05 || (own > 0 && own - fam.rank >= 2));
  if (!lines.length && !gen && !showFam && !mix) return null;

  const maxCount = fam ? Math.max(...fam.members.map((m) => m.count)) : 1;
  const lead = fam ? entryFor(core, fam.members[0].key).name : "";
  const years = mix ? Array.from({ length: mix.shares.length }, (_, i) => mix.y0 + i) : [];

  return (
    <section class="dsec story" aria-labelledby="story-title">
      <h2 id="story-title">What the numbers say</h2>
      {lines.length > 0 && (
        <ul class="story-lines">
          {lines.map((l) => (
            <li key={l}>{l}</li>
          ))}
        </ul>
      )}

      {gen && (
        <div class="story-block">
          <h3>Its generation</h3>
          <p>
            Half of all the {word} named {name} since {start} were born{" "}
            {gen.from === gen.to ? `in ${gen.from}` : `between ${gen.from} and ${gen.to}`}
            {shaded ? ", shaded on the chart above." : "."}
            {gen.before && ` ${name} was already well used in ${start}, when the records begin, so earlier ${word} aren't counted.`}
          </p>
        </div>
      )}

      {showFam && fam && (
        <div class="story-block">
          <h3>Every spelling counted</h3>
          <p>
            In {latest}, {fmt(fam.total)} {word} were given {name} or a name that sounds the same. With every name's spellings
            counted this way, that ranks #{fmt(fam.rank)}
            {own > 0 && fam.rank < own ? `, up from #${fmt(own)} for this spelling alone` : ""}.
            {fam.members[0].key !== k && ` ${lead} is the most common spelling.`}
          </p>
          <ul class="spell-rows">
            {fam.members.slice(0, 6).map((m) => {
              const nm = entryFor(core, m.key).name;
              return (
                <li key={m.key} class={m.key === k ? "this" : ""}>
                  {m.key === k ? (
                    <span class="spell-name">{nm}</span>
                  ) : (
                    <button class="spell-name link-btn" onClick={() => openName(m.key)}>
                      {nm}
                    </button>
                  )}
                  <span class="spell-bar" aria-hidden="true">
                    <i style={{ width: `${Math.max(2, (m.count / maxCount) * 100)}%` }} />
                  </span>
                  <span class="spell-count">
                    {fmt(m.count)}
                    <span class="sr-only"> {word}</span>
                  </span>
                </li>
              );
            })}
          </ul>
          {fam.members.length > 6 && (
            <p class="source-note">
              And {fam.members.length - 6} more spelling{fam.members.length - 6 === 1 ? "" : "s"}.
            </p>
          )}
        </div>
      )}

      {mix && (
        <div class="story-block">
          <h3>Girls and boys</h3>
          <p>{balanceWords(mix, name)}</p>
          <HistoryChart
            years={years}
            series={[{ id: "girls", label: "Girls", cls: "s-girl", values: mix.shares }]}
            mode="share"
            height={110}
            measure={`Girls' share of the babies named ${name} each year`}
          />
        </div>
      )}
    </section>
  );
}
